/**
 * Telegram WebApp SDK integration
 * Handles initData validation, theme, and Telegram-specific features
 */

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    query_id?: string;
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
      is_premium?: boolean;
      photo_url?: string;
    };
    auth_date?: number;
    hash?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor: string;
  backgroundColor: string;
  isClosingConfirmationEnabled: boolean;
  BackButton: {
    isVisible: boolean;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    show: () => void;
    hide: () => void;
  };
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    isProgressVisible: boolean;
    setText: (text: string) => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  ready: () => void;
  expand: () => void;
  close: () => void;
  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;
  onEvent: (eventType: string, callback: () => void) => void;
  offEvent: (eventType: string, callback: () => void) => void;
  sendData: (data: string) => void;
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink: (url: string) => void;
  openInvoice: (url: string, callback?: (status: string) => void) => void;
  showPopup: (params: {
    title?: string;
    message: string;
    buttons?: Array<{ id?: string; type?: string; text: string }>;
  }, callback?: (buttonId: string) => void) => void;
  showAlert: (message: string, callback?: () => void) => void;
  showConfirm: (message: string, callback?: (confirmed: boolean) => void) => void;
  showScanQrPopup: (params: { text?: string }, callback?: (data: string) => boolean) => void;
  closeScanQrPopup: () => void;
  disableVerticalSwipes?: () => void;
}

// Get Telegram WebApp instance
export const getTelegramWebApp = (): TelegramWebApp | null => {
  if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
    return window.Telegram.WebApp;
  }
  return null;
};

// Initialize Telegram WebApp
export const initTelegramWebApp = () => {
  const tg = getTelegramWebApp();
  
  if (tg) {
    // Disable vertical swipes to prevent minimize on scroll
    // Try multiple approaches for compatibility with different SDK versions
    try {
      // Method 1: Direct method call (if available)
      if (typeof tg.disableVerticalSwipes === 'function') {
        tg.disableVerticalSwipes();
      }
      // Method 2: Try accessing via window object directly
      else if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
        const webApp = window.Telegram.WebApp as any;
        if (typeof webApp.disableVerticalSwipes === 'function') {
          webApp.disableVerticalSwipes();
        }
      }
    } catch (e) {
      console.warn('Could not disable vertical swipes:', e);
    }
    
    // Notify Telegram that the Web App is ready
    tg.ready();
    
    // Expand to full height
    tg.expand();
    
    // Set theme colors
    tg.headerColor = '#0B1220';
    tg.backgroundColor = '#0B1220';
    
    // Enable closing confirmation for safety
    tg.enableClosingConfirmation();
    
    console.log('🚀 Telegram WebApp initialized:', {
      version: tg.version,
      platform: tg.platform,
      user: tg.initDataUnsafe.user,
      colorScheme: tg.colorScheme,
    });
    
    return tg;
  }
  
  console.warn('⚠️ Telegram WebApp is not available. Running in development mode.');
  return null;
};

// Get user from Telegram WebApp
export const getTelegramUser = () => {
  const tg = getTelegramWebApp();
  return tg?.initDataUnsafe?.user || null;
};

// Get initData for backend authentication
export const getTelegramInitData = () => {
  const tg = getTelegramWebApp();
  return tg?.initData || '';
};

// Check if running inside Telegram
export const isRunningInTelegram = () => {
  return getTelegramWebApp() !== null;
};

// Check if HapticFeedback is supported (requires WebApp version >= 6.1)
const isHapticSupported = () => {
  const tg = getTelegramWebApp();
  if (!tg) return false;
  
  try {
    // HapticFeedback was added in version 6.1
    const version = parseFloat(tg.version);
    return version >= 6.1;
  } catch {
    return false;
  }
};

// Haptic feedback helpers
export const haptic = {
  light: () => {
    if (isHapticSupported()) {
      getTelegramWebApp()?.HapticFeedback.impactOccurred('light');
    }
  },
  medium: () => {
    if (isHapticSupported()) {
      getTelegramWebApp()?.HapticFeedback.impactOccurred('medium');
    }
  },
  heavy: () => {
    if (isHapticSupported()) {
      getTelegramWebApp()?.HapticFeedback.impactOccurred('heavy');
    }
  },
  success: () => {
    if (isHapticSupported()) {
      getTelegramWebApp()?.HapticFeedback.notificationOccurred('success');
    }
  },
  error: () => {
    if (isHapticSupported()) {
      getTelegramWebApp()?.HapticFeedback.notificationOccurred('error');
    }
  },
  warning: () => {
    if (isHapticSupported()) {
      getTelegramWebApp()?.HapticFeedback.notificationOccurred('warning');
    }
  },
  selection: () => {
    if (isHapticSupported()) {
      getTelegramWebApp()?.HapticFeedback.selectionChanged();
    }
  },
};

// Open Telegram invoice for payments
export const openTelegramInvoice = (invoiceUrl: string): Promise<string> => {
  return new Promise((resolve) => {
    const tg = getTelegramWebApp();
    if (tg) {
      tg.openInvoice(invoiceUrl, (status) => {
        resolve(status);
      });
    } else {
      resolve('failed');
    }
  });
};

// Show alert
export const showAlert = (message: string): Promise<void> => {
  return new Promise((resolve) => {
    const tg = getTelegramWebApp();
    if (tg) {
      tg.showAlert(message, () => resolve());
    } else {
      alert(message);
      resolve();
    }
  });
};

// Show confirm dialog
export const showConfirm = (message: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const tg = getTelegramWebApp();
    if (tg) {
      tg.showConfirm(message, (confirmed) => resolve(confirmed));
    } else {
      resolve(confirm(message));
    }
  });
};

export default {
  getTelegramWebApp,
  initTelegramWebApp,
  getTelegramUser,
  getTelegramInitData,
  isRunningInTelegram,
  haptic,
  openTelegramInvoice,
  showAlert,
  showConfirm,
};



